/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import {
  Image,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import SwipeableViews from "react-swipeable-views";
import Rodal from "rodal";
import {
  Text,
  styles,
  Button,
  FadeInOutView,
  PaginationDots,
} from "UpduoLibrary";
import {
  skalePrimaryBlue,
  skaleWhite,
  skaleSkipGray,
} from "config/UpduoColors";
import { useSelector } from "react-redux";
import { RootState } from "store/types";

import * as UpduoAnalytics from "config/UpduoAnalytics";
import tutorialImage1 from "assets/tutorial-step1.png";
import tutorialImage2 from "assets/tutorial-step2.png";
import tutorialImage4 from "assets/tutorial-step4.png";
import { getResizedImageUrl } from "config/Utilities";

const cardHeight = 170;
const imgSize = 300;
const modalHeight = 560;
const modalWidth = 700;
const step3Height = 240;
const step3Width = 400;
const shadowSpace = 20;

function PreviewShield() {
  return (
    <View
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      }}
    />
  );
}

function TutorialStep3Function() {
  return (
    <View
      style={[
        styles.floatingCard,
        {
          width: step3Width,
          height: step3Height,
          borderRadius: 18,
          shadowRadius: 10,
          top: (imgSize * 1.1 - step3Height) / 2 + shadowSpace,
          left: shadowSpace,
        },
      ]}
    >
      <View
        style={{
          paddingTop: 20,
          paddingBottom: 10,
          alignItems: "center",
          paddingHorizontal: 20,
        }}
      >
        <Text isHeading={true} style={{ fontSize: 20, paddingBottom: 15 }}>
          {"Card 2/4"}
        </Text>
        <Text style={{ fontSize: 16 }}>
          {"Share your proudest company accomplishment so far."}
        </Text>
      </View>
      <View
        style={{
          flex: 1,
          paddingHorizontal: 20,
          paddingVertical: 10,
        }}
      >
        <Text
          style={{
            textAlign: "left",
            fontSize: 14,
            color: skaleSkipGray,
            marginBottom: 20,
          }}
        >
          {"about 2 mins"}
        </Text>
        <Button
          text={"Next"}
          fontSize={16}
          height={55}
          color={skalePrimaryBlue}
          textColor={skaleWhite}
          width={360}
        />
      </View>
      <PreviewShield />
    </View>
  );
}

interface TutorialStepFunctionProps {
  displayShadow?: boolean;
  imageSource: any;
  style?: StyleProp<ViewStyle>;
}

function TutorialStepFunction({
  displayShadow = false,
  imageSource,
  style,
}: TutorialStepFunctionProps) {
  return (
    <View
      style={[
        displayShadow ? styles.floatingCard : null,
        style,
        {
          flex: 1,
          width: imgSize,
          height: imgSize * 1.1,
          justifyContent: "center",
          left: (step3Width - imgSize) / 2 + shadowSpace,
        },
      ]}
    >
      <Image
        style={{
          flex: 1,
          width: undefined,
          height: undefined,
          resizeMode: "contain",
        }}
        source={
          imageSource.uri
            ? {
                uri: getResizedImageUrl(
                  imageSource.uri,
                  imgSize,
                  imgSize * 1.1
                ),
              }
            : imageSource
        }
      />
    </View>
  );
}

export default function TutorialModal({ visible, onClose }) {
  const [stepNumber, setStepNumber] = useState(0);
  const selfObject = useSelector((state: RootState) => state.selfObject);

  const stepCardContent = [
    {
      stepTitle:
        selfObject.firstName && selfObject.firstName !== "Anonymous"
          ? `Hi, ${selfObject.firstName}!`
          : "Welcome!",
      stepContent:
        "Upduo helps you learn and practice new skills with your team.",
    },
    {
      stepTitle: "How to Upduo",
      stepContent: "We’ll connect you with a partner in a 1:1 video call.",
    },
    {
      stepTitle: "Guided lessons",
      stepContent:
        "During the video call, cards will guide you through a lesson.",
    },
    {
      stepTitle: "Have fun!",
      stepContent: "Meet new people and learn together while having fun.",
    },
  ];

  const _handlePreviousButton = () => {
    setStepNumber(stepNumber - 1);
  };

  const _handleNextButton = () => {
    setStepNumber(stepNumber + 1);
  };

  const _handleGetStarted = () => {
    UpduoAnalytics.logEvent("user-tutorial-finish");
    onClose();
    setStepNumber(0);
  };

  const _handleSkipButton = () => {
    UpduoAnalytics.logEvent("user-tutorial-skip");
    onClose();
    setStepNumber(0);
  };

  return (
    <Rodal
      visible={visible}
      customStyles={{
        borderRadius: 12,
        height: modalHeight,
        width: modalWidth,
        display: "flex",
        justifyContent: "center",
        padding: 40,
        backgroundColor: skaleWhite,
      }}
      onClose={onClose}
      showCloseButton={false}
      closeMaskOnClick={false}
    >
      <FadeInOutView
        style={{
          alignItems: "center",
        }}
      >
        <View
          style={{
            flex: 1,
            justifyContent: "space-between",
          }}
        >
          <View
            style={{
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
              overflow: "visible",
            }}
          >
            <View
              style={{
                position: "absolute",
                height: imgSize * 1.1 + shadowSpace * 2,
                width: step3Width + shadowSpace * 2,
                overflow: "visible",
                top: -shadowSpace,
                left: (modalWidth - 120 - step3Width) / 2 - shadowSpace,
              }}
            >
              <SwipeableViews
                disabled={false}
                index={stepNumber}
                onChangeIndex={() => {}}
                style={{ overflow: "hidden" }}
              >
                <TutorialStepFunction
                  displayShadow={true}
                  imageSource={tutorialImage1}
                  style={{
                    shadowRadius: 16,
                    borderRadius: 16,
                    marginTop: shadowSpace - 5,
                  }}
                />
                <TutorialStepFunction
                  imageSource={tutorialImage2}
                  style={{ marginTop: shadowSpace - 5 }}
                />
                <TutorialStep3Function />
                <TutorialStepFunction
                  imageSource={tutorialImage4}
                  style={{ marginTop: 35 }}
                />
              </SwipeableViews>
            </View>
          </View>
          <View
            style={{
              paddingHorizontal: 40,
              paddingTop: 50,
              height: cardHeight,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <View
              style={{
                alignItems: "center",
              }}
            >
              <Text
                isHeading={true}
                style={{
                  fontSize: 22,
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                {stepCardContent[stepNumber].stepTitle}
              </Text>
              <View style={{ height: 10 }} />
              <Text
                style={{
                  fontSize: 18,
                  textAlign: "center",
                  width: 500,
                  marginBottom: 20,
                }}
              >
                {stepCardContent[stepNumber].stepContent}
              </Text>
              <PaginationDots
                currentPosition={stepNumber}
                showOnlyCurrent={true}
              />
            </View>
          </View>
        </View>
        <View
          style={{
            marginTop: 20,
            display: "flex",
            flexDirection: "row",
            width: modalWidth,
          }}
        >
          {stepNumber > 0 ? (
            <View style={{ marginRight: 20 }}>
              <Button
                onPress={_handlePreviousButton}
                text={"Previous"}
                disabledColor={skaleWhite}
                textColor={skalePrimaryBlue}
                color={skaleWhite}
                borderRadius={8}
                height={70}
                width={modalWidth / 2 - 10}
                borderColor={skalePrimaryBlue}
                borderWidth={1}
                hoverColor={skalePrimaryBlue}
                hoverTextColor={skaleWhite}
              />
            </View>
          ) : null}
          {stepNumber === 0 ? (
            <Button
              onPress={_handleNextButton}
              text={"Next"}
              color={skalePrimaryBlue}
              textColor={skaleWhite}
              borderRadius={8}
              height={70}
              width={modalWidth}
              borderColor={skalePrimaryBlue}
              borderWidth={1}
            />
          ) : (
            <Button
              onPress={stepNumber < 3 ? _handleNextButton : _handleGetStarted}
              text={stepNumber < 3 ? "Next" : "Get Started"}
              color={skalePrimaryBlue}
              textColor={skaleWhite}
              height={70}
              width={modalWidth / 2 - 10}
              borderRadius={8}
              borderColor={skalePrimaryBlue}
              borderWidth={1}
            />
          )}
        </View>
      </FadeInOutView>
      <View style={{ position: "absolute", top: 28, right: 40 }}>
        <TouchableOpacity onPress={() => _handleSkipButton()}>
          <Text
            // This gray text almost disappears when clicked with TouchableOpacity.
            // Maybe it should darken instead?
            style={{
              color: skaleSkipGray,
              fontFamily: "ObjectSans-Medium",
              fontWeight: "bold",
            }}
          >
            {"Skip"}
          </Text>
        </TouchableOpacity>
      </View>
    </Rodal>
  );
}
